import React, { useMemo, useState } from "react";
import { useUI } from "@/contexts/UIContext";
import { useAuth } from "@/contexts/AuthContext";
import type { FeatureKey, Tier } from "@/contexts/AccessContext";
import { useAccess } from "@/contexts/AccessContext";

function featureLabel(feature: FeatureKey | undefined): string {
  if (feature === "calculator") return "Lot Size & Risk Calculator";
  if (feature === "plan") return "Trading Plans";
  if (feature === "journal") return "Trade Journal";
  if (feature === "portfolio") return "Portfolio";
  if (feature === "progress") return "Challenge Progress";
  if (feature === "screenshots") return "Screenshots";
  return "this feature";
}

function tierLabel(tier: Tier | undefined): string {
  if (tier === "starter") return "STARTER";
  if (tier === "pro") return "PRO";
  if (tier === "elite") return "ELITE";
  return "STARTER";
}

export const UpgradeModal: React.FC = () => {
  const { isUpgradeModalOpen, upgradeModal, closeUpgradeModal } = useUI();
  const { user, signInWithGoogle } = useAuth();
  const { betaExpired } = useAccess();
  const [busy, setBusy] = useState(false);

  const content = useMemo(() => {
    const feature = upgradeModal?.feature;
    const requiredTier = upgradeModal?.requiredTier;
    const reason = upgradeModal?.reason;

    if (!upgradeModal || !isUpgradeModalOpen) {
      return {
        title: "",
        body: "",
        primaryText: "",
        primaryAction: null as null | (() => void | Promise<void>),
        secondaryHref: null as null | string,
        secondaryText: "",
      };
    }

    if (reason === "login_required") {
      return {
        title: "Sign in required",
        body: `To use ${featureLabel(feature)}, please sign in with Google. You can keep using the calculator for free without signing in.`,
        primaryText: "Continue with Google",
        primaryAction: async () => {
          setBusy(true);
          try {
            await signInWithGoogle();
          } finally {
            setBusy(false);
          }
        },
        secondaryHref: null,
        secondaryText: "",
      };
    }

    if (reason === "limit_reached") {
      const limit = upgradeModal.limit;
      const current = upgradeModal.current;
      const limitLine =
        typeof limit === "number" && typeof current === "number"
          ? `Current: ${current} · Limit: ${limit}.`
          : "";

      return {
        title: "Limit reached",
        body: `You’ve reached the limit for your current tier. ${limitLine} Upgrade to continue adding more.`,
        primaryText: "Contact support",
        primaryAction: null,
        secondaryHref: "mailto:support@propviper.com?subject=PropViper%20Upgrade%20Request",
        secondaryText: "Email support@propviper.com",
      };
    }

    if (betaExpired) {
      return {
        title: "Your beta access has ended",
        body:
          "You’ve been using PropViper’s full calculator, trading plan, and journal features during the beta.\n\n" +
          "To continue using these features across devices and keep your trading data in sync, upgrade to a paid plan.\n\n" +
          "Your existing data is محفوظ and will remain available.",
        primaryText: "Continue with Pro",
        primaryAction: null,
        secondaryHref: "/pricing",
        secondaryText: "View pricing",
      };
    }

    return {
      title: "Upgrade required",
      body: `This action is available on ${tierLabel(requiredTier)}. You can keep using the calculator for free, or upgrade when you’re ready.`,
      primaryText: "Contact support",
      primaryAction: null,
      secondaryHref: "mailto:support@propviper.com?subject=PropViper%20Upgrade%20Request",
      secondaryText: "Email support@propviper.com",
    };
  }, [betaExpired, isUpgradeModalOpen, signInWithGoogle, upgradeModal]);

  if (!isUpgradeModalOpen || !upgradeModal) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onClick={() => closeUpgradeModal()}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-neutral-800 bg-neutral-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-neutral-800">
          <div className="text-sm font-semibold text-neutral-100">{content.title}</div>
          <button
            type="button"
            onClick={() => closeUpgradeModal()}
            className="text-neutral-300 hover:text-neutral-100 text-sm font-semibold"
          >
            Close
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="text-sm text-neutral-200">{content.body}</div>

          {upgradeModal.feature && (
            <div className="mt-3 rounded-xl bg-neutral-900/40 border border-neutral-800 p-3 text-xs text-neutral-400">
              Requested: <span className="text-neutral-200 font-semibold">{featureLabel(upgradeModal.feature)}</span>
              {user ? null : <span className="text-neutral-500"> · Not signed in</span>}
            </div>
          )}

          <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:justify-end">
            <button
              type="button"
              onClick={() => closeUpgradeModal()}
              className="bg-neutral-950 hover:bg-neutral-900 text-neutral-200 border border-neutral-800 px-4 py-2 rounded-xl text-xs font-semibold transition"
            >
              Not now
            </button>

            {content.secondaryHref && content.secondaryText ? (
              <a
                href={content.secondaryHref}
                className="bg-neutral-800/60 hover:bg-neutral-800 text-neutral-100 border border-neutral-700 px-4 py-2 rounded-xl text-xs font-semibold transition text-center"
              >
                {content.secondaryText}
              </a>
            ) : null}

            {content.primaryAction ? (
              <button
                type="button"
                onClick={async () => {
                  await content.primaryAction?.();
                }}
                disabled={busy}
                className="bg-yellow-400 text-black font-semibold px-4 py-2 rounded-xl hover:bg-yellow-300 transition text-xs disabled:opacity-60"
              >
                {busy ? "Please wait..." : content.primaryText}
              </button>
            ) : (
              <a
                href={betaExpired ? "/pricing?plan=pro" : "mailto:support@propviper.com?subject=PropViper%20Upgrade%20Request"}
                className="bg-yellow-400 text-black font-semibold px-4 py-2 rounded-xl hover:bg-yellow-300 transition text-xs text-center"
              >
                {content.primaryText}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
